import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Typography,
  Button,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  Tooltip,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BuildIcon from '@mui/icons-material/Build';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EditIcon from '@mui/icons-material/Edit';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { useTranslation } from 'react-i18next';
import { useStartAgentChat } from '../../hooks/useStartAgentChat';
import { Z_INDEX } from '../../constants/zIndex';
import { getInitials } from '../editor/features/roles/utils/rolColorUtils';
import { prepareRoleColorForChat } from '../../utils/roleColorValidation';
import { markAgentSeen } from '../../utils/markAgentSeen';

/**
 * AgentDetailModal — Full agent detail dialog.
 * Shows description, capabilities (tools), and "Start Chat" button.
 * When isOwned=true, also shows an "Edit" button.
 */
const AgentDetailModal = ({ open, onClose, role, isOwned = false, onEdit }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const startAgentChat = useStartAgentChat();

  // #759 D20: abrir su ficha también cuenta como abrir el agente.
  useEffect(() => {
    if (open) markAgentSeen(role);
  }, [open, role]);

  if (!role) return null;

  const bgColor = role.color || theme.palette.primary.main;
  const roleColors = prepareRoleColorForChat(bgColor, theme);
  const tools = role.tools || [];

  const handleStartChat = () => {
    onClose();
    startAgentChat(role);
  };

  const handleEdit = () => {
    onClose();
    onEdit?.(role);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      data-testid="agent-detail-modal"
      sx={{
        zIndex: Z_INDEX.MODALS,
        '& .MuiDialog-paper': { borderRadius: 2, maxHeight: '80vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 6 }}>
        <Avatar
          sx={{
            width: 64,
            height: 64,
            fontSize: '1.5rem',
            fontWeight: 600,
            background: roleColors.avatarBgGradient,
            boxShadow: roleColors.avatarShadow,
            color: roleColors.avatarText,
          }}
        >
          {getInitials(role.name)}
        </Avatar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
            {role.name}
          </Typography>
          {role.isSystem && (
            <Tooltip title={t('agents.systemTooltip', 'Agente del sistema — puedes personalizar instrucciones y objetivos')} arrow>
              <Chip
                icon={<ShieldOutlinedIcon sx={{ fontSize: '0.85rem' }} />}
                label={t('agents.systemBadge', 'Sistema')}
                size="small"
                variant="outlined"
                sx={{
                  height: 24,
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  borderColor: 'divider',
                  '& .MuiChip-icon': { color: 'text.secondary' },
                  flexShrink: 0,
                }}
              />
            </Tooltip>
          )}
        </Box>
        <IconButton
          onClick={onClose}
          aria-label={t('common.close', 'Close')}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Description */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('explore.detail.description', 'Description')}
          </Typography>
          <Typography variant="body1">
            {role.objectives || t('explore.detail.noDescription', 'No description available')}
          </Typography>
        </Box>

        {/* Capabilities */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('explore.detail.capabilities', 'Capabilities')}
          </Typography>
          {tools.length > 0 ? (
            <List dense disablePadding>
              {tools.map((tool) => (
                <ListItem key={tool.id} disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <BuildIcon fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText primary={tool.name} />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('explore.detail.noCapabilities', 'No specific tools configured')}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
        {isOwned && (
          <Button
            variant="outlined"
            size="large"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            data-testid="edit-agent-button"
          >
            {t('explore.editAgent', 'Edit')}
          </Button>
        )}
        <Button
          variant="contained"
          size="large"
          startIcon={<PlayArrowIcon />}
          onClick={handleStartChat}
          fullWidth
          data-testid="start-chat-button"
        >
          {t('explore.startChat', 'Start Chat')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentDetailModal;
